import {
  KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {useQueryClient} from 'react-query';
import styled from 'styled-components';
import {CancelIcon} from '@ui/atoms/icons';
import {lockElementScroll} from '@utils/scroll-lock';
import CreateFeedback from './create-feedback/create-feedback';
import RequestFeedback from './request-feedback/request-feedback';
import {FeedbackRequest} from './feedback-items';

export type FeedbackSidecarView =
  | 'send-feedback'
  | 'post-praise'
  | 'request-feedback'
  | 'respond';

const Overlay = styled.div`
  position: fixed;
  z-index: 1200;
  inset: 0;
  background: rgba(30, 30, 47, 0.48);
`;

const Panel = styled.aside`
  position: fixed;
  z-index: 1201;
  top: 0;
  right: 0;
  display: flex;
  width: min(560px, 100vw);
  height: 100vh;
  flex-direction: column;
  background: ${({theme}) => theme.colors.white};
  box-shadow: -8px 0 32px rgba(30, 30, 47, 0.12);
`;

const Header = styled.header`
  display: flex;
  min-height: 80px;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  gap: 24px;
  border-bottom: 1px solid ${({theme}) => theme.colors.borderLight};
  padding: 18px 24px;

  @media (max-width: 560px) {
    padding: 14px 20px;
  }
`;

const Title = styled.h2`
  margin: 0;
  color: ${({theme}) => theme.colors.blackTwo};
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
`;

const CloseButton = styled.button`
  display: inline-flex;
  width: 103px;
  height: 42px;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  border: 1px solid ${({theme}) => theme.colors.borderDark};
  border-radius: 10px;
  background: ${({theme}) => theme.colors.white};
  padding: 10px 16px;
  color: ${({theme}) => theme.colors.blackTwo};
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;

  svg {
    width: 16px;
    height: 16px;
  }

  &:focus-visible {
    outline: 2px solid ${({theme}) => theme.colors.purple300};
    outline-offset: 2px;
  }
`;

const ScrollArea = styled.div`
  width: 100%;
  min-height: 0;
  min-width: 0;
  flex: 1;
  overflow-y: auto;
  padding: 24px 24px 32px;

  > * {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  @media (max-width: 560px) {
    padding: 20px 20px 28px;
  }
`;

const titles: Record<FeedbackSidecarView, string> = {
  'send-feedback': 'Send feedback',
  'post-praise': 'Post praise',
  'request-feedback': 'Request feedback',
  respond: 'Respond to feedback request',
};

const feedbackFeedQueryKeys = [
  'public-wall-feedback',
  'my-feedback',
  'feedback-written',
  'feedback-requests-sent',
];

interface FeedbackSidecarProps {
  view: FeedbackSidecarView;
  request?: FeedbackRequest;
  requestId?: string | null;
  onRequestCompleted?: (requestId: string) => void;
  onClose: () => void;
}

export const FeedbackSidecar = ({
  view,
  request,
  requestId,
  onRequestCompleted,
  onClose,
}: FeedbackSidecarProps) => {
  const panelRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  const queryClient = useQueryClient();

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const releaseScrollLock = lockElementScroll(document.body);
    closeButtonRef.current?.focus();

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      releaseScrollLock();
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleSuccess = useCallback(async () => {
    await Promise.all(
      feedbackFeedQueryKeys.map((queryKey) =>
        queryClient.invalidateQueries(queryKey, {
          refetchActive: false,
          refetchInactive: false,
        }),
      ),
    );
    await Promise.all(
      feedbackFeedQueryKeys.map((queryKey) =>
        queryClient.refetchQueries(queryKey, {active: true}),
      ),
    );
    if (view === 'respond' && requestId) {
      onRequestCompleted?.(requestId);
    }
    onClose();
  }, [onClose, onRequestCompleted, queryClient, requestId, view]);

  const trapFocus = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Tab') return;
    const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable?.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      <Overlay aria-hidden="true" onClick={onClose} />
      <Panel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-sidecar-title"
        onKeyDown={trapFocus}>
        <Header>
          <Title id="feedback-sidecar-title">{titles[view]}</Title>
          <CloseButton ref={closeButtonRef} type="button" onClick={onClose}>
            <CancelIcon />
            Close
          </CloseButton>
        </Header>
        <ScrollArea>
          {view === 'request-feedback' ? (
            <RequestFeedback embedded onSuccess={handleSuccess} />
          ) : (
            <CreateFeedback
              embedded
              feedbackType={view === 'post-praise' ? 'praise' : 'feedback'}
              requestId={view === 'respond' ? requestId : null}
              prefill={
                view === 'respond' && request
                  ? {
                      user:
                        typeof request.user === 'string'
                          ? request.user
                          : (request.user as any)?.id,
                      subjectType: request.subjectType,
                      subjectName: request.subjectName,
                      subjectId: request.subjectId,
                    }
                  : undefined
              }
              onSuccess={handleSuccess}
            />
          )}
        </ScrollArea>
      </Panel>
    </>
  );
};
